import * as React from 'react';

import {
  AdminAutoMessages_organizations,
  AdminAutoMessages_organizations_autoMessages,
  AdminAutoMessages_organizations_autoMessages_organization,
  AdminAutoMessages_organizations_autoMessages_sender,
  AdminAutoMessages_organizations_autoMessages_messageContent,
} from './queries/__generated__/AdminAutoMessages';
import AdminNewAutoMessageModal, { AutoMessageTabKey } from 'admin/components/AdminNewAutoMessage';
import {
  ComposableTable,
  ColumnGeneratorFunc,
  composeMultipleCells,
  getErrorTextCellGenerator,
  getMenuButtonCellGenerator,
  getTextBadgeCellGenerator,
  getTextCellGenerator,
  getTimestampCellGenerator,
} from 'components/ComposableTable';
import {
  deleteAutoMessage,
  duplicateAutoMessage,
  setAutoMessageStatus,
  // @ts-ignore
} from 'admin/actions/AutoMessageActions';

// @ts-ignore
import AdminAutoMessagesDeleteConfirmDialog from './AdminAutoMessagesDeleteConfirmDialog';
import AdminAutoMessagesNull from './AdminAutoMessagesNull';
import Button from 'components/Button';
import CenteredLoadingIndicator from 'components/CenteredLoadingIndicator';
import ErrorReporter from 'app/components/ErrorReporter';
import Heading from 'components/Heading';
import { InjectedRouter } from 'react-router';
import MediaObject from 'components/MediaObject';
import Modal from 'react-modal';
import Row from 'components/Row';
import { SaveAutoMessageInput, AutoMessageStatus } from 'globalTypes';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import { statusToTextBadge } from 'admin/constants/AutoMessages';
import { withRouter } from 'app/context/Router';

type AutoMessageRowData = {
  uuid: string;
  messageName: string;
  includeParents: boolean;
  includeStudents: boolean;
  csvName: React.ReactNode;
  organization: AdminAutoMessages_organizations_autoMessages_organization;
  orgName: string;
  recipientLabel: React.ReactNode;
  sender: AdminAutoMessages_organizations_autoMessages_sender | null;
  status: AutoMessageStatus;
  messageContent: AdminAutoMessages_organizations_autoMessages_messageContent | null;
  lastSendTime: any;
  lastSendErrorCount: number;
  lastSendErrorLabel: string;
  canEdit: boolean;
  canActivate: boolean;
  canPause: boolean;
  customMessageFields: string[] | null;
};

type Props = {
  loading?: boolean;
  refetchAutoMessagesForOrg?: () => void;
  organization: AdminAutoMessages_organizations;
  saveAutoMessage: (input: SaveAutoMessageInput) => Promise<void>;
  onClickNewAutoMessage: () => void;
  urlSlug: string;
  viewingOrgUuid: string;
  router: InjectedRouter;
};

type State = {
  isConfirmDeleteDialogOpened: boolean;
  isNewAutoMessageModalOpened: boolean;
  autoMessageModalMessageUuid?: string;
  autoMessageModalStartingTab?: AutoMessageTabKey;
  deleteAutoMessageUuid?: string;
  deleteAutoMessageName?: string;
};

class AdminAutoMessagesView extends React.Component<Props, State> {
  state: State = {
    isNewAutoMessageModalOpened: false,
    isConfirmDeleteDialogOpened: false,
    autoMessageModalMessageUuid: undefined,
    autoMessageModalStartingTab: undefined,
    deleteAutoMessageUuid: undefined,
    deleteAutoMessageName: undefined,
  };

  showAutoMessageDialog = (
    autoMessageModalMessageUuid: string,
    autoMessageModalStartingTab?: AutoMessageTabKey
  ) => {
    this.setState(
      {
        autoMessageModalMessageUuid,
        autoMessageModalStartingTab,
      },
      () => this.setState({ isNewAutoMessageModalOpened: true })
    );
  };

  hideAutoMessageDialog = () => {
    this.setState({
      autoMessageModalStartingTab: undefined,
      autoMessageModalMessageUuid: undefined,
      isNewAutoMessageModalOpened: false,
    });
  };

  showConfirmDeleteDialog = (deleteAutoMessageUuid: string, deleteAutoMessageName: string) => {
    this.setState(
      {
        deleteAutoMessageUuid,
        deleteAutoMessageName,
      },
      () => this.setState({ isConfirmDeleteDialogOpened: true })
    );
  };

  hideConfirmDeleteDialog = () => {
    this.setState({
      deleteAutoMessageUuid: undefined,
      deleteAutoMessageName: undefined,
      isConfirmDeleteDialogOpened: false,
    });
  };

  handleDelete = () => {
    const { deleteAutoMessageUuid, deleteAutoMessageName } = this.state;
    if (deleteAutoMessageUuid) {
      deleteAutoMessage(
        deleteAutoMessageUuid,
        deleteAutoMessageName || 'This message',
        this.props.organization.uuid,
        () => undefined,
        () => this.hideConfirmDeleteDialog()
      );
      return;
    }
    console.error('AdminAutoMessagesView - tried to delete an auto message without a uuid');
    ToastActions.showErrorToast('Oops! Looks like something went wrong.');
  };

  goToMessageDetail = (uuid: string) => {
    if (this.props.router) {
      this.props.router.push(`${this.props.urlSlug}/${uuid}`);
    }
  };

  formatStudentAndParentLabel = (includeParents: boolean, includeStudents: boolean) => {
    const parentText = includeParents ? 'Parents and guardians' : null;
    const studentText = includeStudents ? 'Students' : null;
    return [studentText, parentText].filter(text => text).join(', ');
  };

  renderEmptySubtextComponent = (
    canEdit: boolean,
    dataTestKey: string,
    messageUuid: string,
    step: AutoMessageTabKey,
    buttonLabel: string,
    nonButtonLabel: string
  ) => {
    const openModalAction = (e: React.MouseEvent) => {
      e.stopPropagation();
      this.showAutoMessageDialog(messageUuid, step);
    };
    if (canEdit) {
      return (
        <Button
          data-test={dataTestKey}
          level="icon"
          color="brand"
          onClick={e => openModalAction(e)}>
          {buttonLabel}
        </Button>
      );
    }
    return <Text italic>{nonButtonLabel}</Text>;
  };

  formatDataIntoRow = (
    message: AdminAutoMessages_organizations_autoMessages
  ): AutoMessageRowData => {
    const {
      isActivatable,
      lastSend,
      messageName,
      messageContent,
      csvName,
      creatorOrganization,
      organization,
      includeParents,
      includeStudents,
      status,
      sender,
      uuid,
      customMessageFields,
    } = message;

    const canEdit = creatorOrganization ? creatorOrganization.isAdmin : false;
    const lastSendTime = lastSend?.sentAt;
    const lastSendErrorCount =
      (lastSend?.readSummary?.unreachableEntry?.count ?? 0) +
      (lastSend?.readSummary?.identificationErrorEntry?.count ?? 0);
    const lastSendErrorLabel =
      lastSendErrorCount > 0 ? `${lastSendErrorCount} errors` : 'No errors';

    return {
      uuid,
      messageName,
      includeParents,
      includeStudents,
      csvName:
        csvName ||
        this.renderEmptySubtextComponent(
          canEdit,
          'empty-csv-name-button',
          uuid,
          'format-csv',
          'Add filename',
          'Filename not yet defined'
        ),
      organization,
      orgName: organization.name,
      recipientLabel:
        this.formatStudentAndParentLabel(includeParents, includeStudents) ||
        this.renderEmptySubtextComponent(
          canEdit,
          'empty-recipients-button',
          uuid,
          'compose',
          'Assign message recipients',
          'Recipients not yet selected'
        ),
      sender,
      status,
      messageContent,
      lastSendTime,
      lastSendErrorCount,
      lastSendErrorLabel: lastSendTime ? lastSendErrorLabel : 'No sends yet',
      canEdit,
      canActivate: isActivatable && status !== 'active',
      canPause: status === 'active',
      customMessageFields,
    };
  };

  render() {
    const { loading, organization, onClickNewAutoMessage } = this.props;

    if (loading) {
      return <CenteredLoadingIndicator />;
    }

    if (!organization) {
      return (
        <ErrorReporter error="AdminAutoMessagesView - tried to render without an organization" />
      );
    }
    const { childOrganizations } = organization;

    if (organization && organization.autoMessages.length === 0) {
      // TODO: move logic to apollo
      const noOrgsWithGoodStudentIdPercentage =
        organization.studentIdPercentage < 0.01 &&
        !childOrganizations.some(org => org && org.studentIdPercentage > 0.01);
      return (
        <AdminAutoMessagesNull
          orgUuid={organization.uuid}
          showStudentIdPercentageWarning={noOrgsWithGoodStudentIdPercentage}
          onClickNewAutoMessage={onClickNewAutoMessage}
        />
      );
    }

    const columnHeaders = ['Name', 'Status', 'Recipients', 'Last sent', ''].map(name => ({ name }));

    const columnGenerators: ColumnGeneratorFunc<AutoMessageRowData>[] = [
      composeMultipleCells([
        getTextCellGenerator('messageName', {
          lineHeight: 'xlarge',
          size: 'small',
          weight: 'semibold',
          element: 'div',
        }),
        getTextCellGenerator('csvName', { color: 'thunder', size: 'xxsmall', element: 'div' }),
      ]),
      getTextBadgeCellGenerator('status', statusToTextBadge),
      composeMultipleCells([
        getTextCellGenerator('orgName', {
          lineHeight: 'xlarge',
          size: 'small',
          element: 'div',
        }),
        getTextCellGenerator('recipientLabel', {
          color: 'thunder',
          size: 'xxsmall',
          element: 'div',
        }),
      ]),
      composeMultipleCells([
        getTimestampCellGenerator('lastSendTime', {
          lineHeight: 'xlarge',
          size: 'small',
          element: 'div',
        }),
        getErrorTextCellGenerator('lastSendErrorLabel', 'lastSendErrorCount', { color: 'thunder' }),
      ]),
      getMenuButtonCellGenerator<AutoMessageRowData>([
        {
          conditionToCheck: 'canActivate',
          label: 'Activate',
          icon: 'play-circle',
          fieldsForAction: ['uuid', 'messageName'],
          onClickAction: (data: AutoMessageRowData) =>
            setAutoMessageStatus(data.uuid, 'active', organization.uuid, data.messageName),
        },
        {
          conditionToCheck: 'canPause',
          label: 'Pause',
          icon: 'pause-circle',
          fieldsForAction: ['uuid', 'messageName'],
          onClickAction: (data: AutoMessageRowData) =>
            setAutoMessageStatus(data.uuid, 'inactive', organization.uuid, data.messageName),
        },
        {
          conditionToCheck: 'canEdit',
          label: 'Edit',
          glyph: 'pencil-stroke',
          fieldsForAction: ['uuid'],
          onClickAction: (data: AutoMessageRowData) => this.showAutoMessageDialog(data.uuid),
        },
        {
          conditionToCheck: undefined,
          label: 'Duplicate',
          icon: 'copy',
          fieldsForAction: [
            'messageName',
            'organization',
            'includeParents',
            'includeStudents',
            'messageContent',
            'sender',
            'customMessageFields',
          ],
          onClickAction: (data: AutoMessageRowData) =>
            duplicateAutoMessage(data, organization.uuid),
        },
        {
          conditionToCheck: undefined,
          label: 'View sent messages',
          glyph: 'email-transcript',
          fieldsForAction: ['uuid'],
          onClickAction: (data: AutoMessageRowData) => this.goToMessageDetail(data.uuid),
        },
        {
          conditionToCheck: 'canEdit',
          label: 'Delete',
          glyph: 'trash',
          fieldsForAction: ['uuid', 'messageName'],
          onClickAction: (data: AutoMessageRowData) =>
            this.showConfirmDeleteDialog(data.uuid, data.messageName),
          divided: true,
        },
      ]),
    ];

    const rows = organization.autoMessages.map(message => this.formatDataIntoRow(message));

    return (
      <React.Fragment>
        <Modal isOpen={this.state.isNewAutoMessageModalOpened}>
          <AdminNewAutoMessageModal
            onBack={this.hideAutoMessageDialog}
            viewingOrgUuid={organization.uuid}
            messageUuid={this.state.autoMessageModalMessageUuid}
            startingTab={this.state.autoMessageModalStartingTab}
          />
        </Modal>
        <Modal isOpen={this.state.isConfirmDeleteDialogOpened}>
          <AdminAutoMessagesDeleteConfirmDialog
            messageName={this.state.deleteAutoMessageName}
            onConfirm={this.handleDelete}
            onRequestHide={this.hideConfirmDeleteDialog}
          />
        </Modal>
        <Row size="small">
          <MediaObject figure="right" center>
            <Heading flush>Auto messages</Heading>
            <Button data-test="new-auto-message-button" onClick={onClickNewAutoMessage}>
              New auto message
            </Button>
          </MediaObject>
        </Row>
        <ComposableTable
          columnHeaders={columnHeaders}
          columnGenerators={columnGenerators}
          rows={rows}
          keyField="uuid"
          actionsColumn
          handleRowClick={(row: AutoMessageRowData) => this.goToMessageDetail(row.uuid)}
        />
      </React.Fragment>
    );
  }
}

export default withRouter(AdminAutoMessagesView);
